/**
 * WhatsApp Chat Selector
 * 
 * Allows users to select which WhatsApp chats to sync for N71's knowledge graph.
 * Shows a list of recent chats with toggle switches.
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

// Use secure proxy edge function (secret is added server-side)
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

interface Chat {
  jid: string;
  name: string;
  isGroup: boolean;
  lastMessageTime?: number;
  unreadCount?: number;
  messageCount?: number;
}

// Extract phone number from JID for display
function extractPhoneNumber(jid: string): string {
  return jid.replace('@s.whatsapp.net', '').replace('@g.us', '');
}

// Check if the name is just the phone number (meaning no contact name was found)
function isPhoneNumberOnly(name: string, jid: string): boolean {
  const phone = extractPhoneNumber(jid);
  return name === phone || name === jid || !name;
}

// Format phone number for display (add spaces for readability)
function formatPhoneNumber(phone: string): string {
  // Remove any non-digit characters except leading +
  const cleaned = phone.replace(/[^\d+]/g, '');
  
  // If it's a reasonable length, format it
  if (cleaned.length >= 10) {
    // Keep last 4 digits, group the rest
    const last4 = cleaned.slice(-4);
    const middle = cleaned.slice(-7, -4);
    const first = cleaned.slice(0, -7);
    if (first && middle) {
      return `${first} ${middle} ${last4}`;
    }
  }
  return phone;
}

interface WhatsAppChatSelectorProps {
  sessionId: string;
  onConfirm: (selectedChats: Chat[]) => void;
  onSkip: () => void;
  isLoading?: boolean;
}

// Format timestamp to relative time
function formatRelativeTime(timestamp: number): string {
  const now = Date.now() / 1000;
  const diff = now - timestamp;
  
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  if (diff < 2592000) return `${Math.floor(diff / 604800)}w ago`;
  return `${Math.floor(diff / 2592000)}mo ago`;
}

export function WhatsAppChatSelector({
  sessionId,
  onConfirm,
  onSkip,
  isLoading = false,
}: WhatsAppChatSelectorProps) {
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedJids, setSelectedJids] = useState<Set<string>>(new Set());
  const [isLoadingChats, setIsLoadingChats] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'contacts' | 'groups'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch chats via secure proxy with retry
  const fetchChats = useCallback(async () => {
    setIsLoadingChats(true);
    setError(null);

    // Retry up to 10 times (30 seconds total)
    const maxRetries = 10;
    const retryDelay = 3000;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        // Get current session for auth
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('Not authenticated');
        }

        // Call secure proxy (adds bridge secret server-side)
        const response = await fetch(
          `${SUPABASE_URL}/functions/v1/whatsapp-bridge-proxy/sessions/${sessionId}/chats`,
          {
            headers: {
              'Authorization': `Bearer ${session.access_token}`,
              'apikey': SUPABASE_ANON_KEY,
            },
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch chats');
        }

        const data = await response.json();
        
        if (!data.success) {
          throw new Error(data.error || 'Failed to load chats');
        }

        const fetchedChats = data.chats || [];

        // If we got chats, we're done
        if (fetchedChats.length > 0) {
          // Sort by lastMessageTime desc so the most-recent chats surface at
          // the top. Baileys returns chats in storage order — effectively
          // random on a fresh pair — so without this the user had to scroll
          // past years-old contacts to find the ones they actually wanted.
          const sorted = [...fetchedChats].sort((a: Chat, b: Chat) => {
            const aT = a.lastMessageTime ?? 0;
            const bT = b.lastMessageTime ?? 0;
            return bT - aT;
          });
          setChats(sorted);

          // DO NOT pre-select anything. The previous behaviour was to pick
          // the first 10 non-group chats off the bridge's storage-order list
          // — which on fresh connect is effectively random and heavily
          // biased toward old contacts. Aboudi's report ("it keeps adding
          // people I haven't spoken to in years") was exactly this: the
          // auto-selected ghosts were below the fold, so he'd add his
          // real picks and ship extra old contacts without knowing.
          //
          // Let the user pick explicitly. It's a one-time setup step and
          // the list is now sorted by recency so the picks are fast.
          setSelectedJids(new Set());
          setIsLoadingChats(false);
          return;
        }

        // No chats yet - wait and retry
        if (attempt < maxRetries - 1) {
          console.log(`No chats yet, retrying... (${attempt + 1}/${maxRetries})`);
          await new Promise(r => setTimeout(r, retryDelay));
        }
        
      } catch (err) {
        console.error('Failed to fetch chats:', err);
        if (attempt === maxRetries - 1) {
          setError(err instanceof Error ? err.message : 'Failed to load chats');
        }
      }
    }
    
    // After all retries, show helpful message
    setError('WhatsApp is still syncing. This can take up to a minute. Please wait and try again.');
    setIsLoadingChats(false);
  }, [sessionId]);

  useEffect(() => {
    fetchChats();
  }, [fetchChats]);

  // Toggle chat selection
  const toggleChat = (jid: string) => {
    setSelectedJids(prev => {
      const next = new Set(prev);
      if (next.has(jid)) {
        next.delete(jid);
      } else {
        next.add(jid);
      }
      return next;
    });
  };

  // Select every chat in the workspace regardless of the current filter or
  // search. If everything is already selected, this flips to a deselect-all.
  const toggleSelectAll = () => {
    const allSelected = chats.length > 0 && chats.every(c => selectedJids.has(c.jid));
    if (allSelected) {
      setSelectedJids(new Set());
    } else {
      setSelectedJids(new Set(chats.map(c => c.jid)));
    }
  };

  // Select/deselect only the chats currently visible under the active
  // filter + search. Useful when you want to add "all my groups" on top of
  // an existing selection without touching non-group chats.
  const toggleSelectVisible = () => {
    const visibleJids = filteredChats.map(c => c.jid);
    const allVisibleSelected = visibleJids.every(jid => selectedJids.has(jid));

    setSelectedJids(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleJids.forEach(jid => next.delete(jid));
      } else {
        visibleJids.forEach(jid => next.add(jid));
      }
      return next;
    });
  };

  const hasFilterActive = filter !== 'all' || searchQuery.length > 0;
  const everySelected = chats.length > 0 && selectedJids.size === chats.length;

  // Filter chats
  const filteredChats = chats.filter(chat => {
    // Apply type filter
    if (filter === 'contacts' && chat.isGroup) return false;
    if (filter === 'groups' && !chat.isGroup) return false;
    
    // Apply search filter
    if (searchQuery) {
      return chat.name.toLowerCase().includes(searchQuery.toLowerCase());
    }
    
    return true;
  });

  // Confirm selection
  const handleConfirm = () => {
    const selectedChats = chats.filter(c => selectedJids.has(c.jid));
    if (selectedChats.length === 0) {
      toast.error('Please select at least one chat to sync');
      return;
    }
    onConfirm(selectedChats);
  };

  return (
    // Flex column that fills the modal: static sections stay fixed, only
    // the chat list scrolls. Without this the whole stack overflowed the
    // modal's max-h-[80vh] and the action buttons were clipped off the
    // bottom with no way to reach them.
    <div className="flex flex-col flex-1 min-h-0 gap-4">
      {/* Header */}
      <div className="text-center flex-shrink-0">
        <h3 className="text-lg font-medium text-foreground">
          Select Chats to Sync
        </h3>
        <p className="text-sm text-foreground/50 mt-1">
          Choose which conversations N71 should learn from
        </p>
        {/* Refresh button — after pairing, Baileys streams chats + group
            metadata in over ~1-5 minutes. Users who open the picker too
            early see a short list with most groups missing. Let them
            refetch on demand instead of making them close + reopen. */}
        {!isLoadingChats && !error && (
          <button
            onClick={fetchChats}
            className="mt-2 text-xs text-foreground/40 hover:text-foreground/70 transition-colors underline decoration-dotted"
            title="Re-check the bridge for newly discovered chats and groups"
          >
            Missing groups or contacts? Refresh the list
          </button>
        )}
      </div>

      {/* Loading state */}
      {isLoadingChats && (
        <div className="flex flex-col items-center justify-center py-12">
          <motion.div
            className="w-8 h-8 border-2 border-foreground/10 border-t-[#25D366] rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
          <p className="text-sm text-foreground/50 mt-3">Loading your chats...</p>
        </div>
      )}

      {/* Error state */}
      {error && !isLoadingChats && (
        <div className="text-center py-8">
          <p className="text-sm text-red-500 mb-3">{error}</p>
          <button
            onClick={fetchChats}
            className="text-sm text-foreground/60 hover:text-foreground underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* Chat list */}
      {!isLoadingChats && !error && (
        <>
          {/* Filters */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search chats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-foreground/5 border border-foreground/10 rounded-lg text-foreground placeholder:text-foreground/30 focus:outline-none focus:ring-1 focus:ring-[#25D366]/50"
              />
            </div>
            <div className="flex gap-1 bg-foreground/5 rounded-lg p-1">
              {(['all', 'contacts', 'groups'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={cn(
                    "px-3 py-1 text-xs rounded-md transition-colors",
                    filter === f 
                      ? "bg-foreground/10 text-foreground" 
                      : "text-foreground/50 hover:text-foreground"
                  )}
                >
                  {f === 'all' ? 'All' : f === 'contacts' ? 'People' : 'Groups'}
                </button>
              ))}
            </div>
          </div>

          {/* Selection controls */}
          <div className="flex items-center justify-between text-xs text-foreground/50 flex-shrink-0">
            <div className="flex items-center gap-3">
              <button
                onClick={toggleSelectAll}
                className="hover:text-foreground transition-colors"
              >
                {everySelected
                  ? 'Deselect all'
                  : `Select all (${chats.length})`}
              </button>
              {hasFilterActive && !everySelected && filteredChats.length > 0 && (
                <>
                  <span className="text-foreground/20">·</span>
                  <button
                    onClick={toggleSelectVisible}
                    className="hover:text-foreground transition-colors"
                  >
                    {filteredChats.every(c => selectedJids.has(c.jid))
                      ? 'Deselect visible'
                      : `Select visible (${filteredChats.length})`}
                  </button>
                </>
              )}
            </div>
            <span>
              {selectedJids.size} selected
            </span>
          </div>

          {/* Chat list — the only scrolling region; flex-1 so it shrinks
              to fit and the action buttons below stay visible. */}
          <div className="flex-1 min-h-0 overflow-y-auto space-y-1 pr-1">
            <AnimatePresence mode="popLayout">
              {filteredChats.length === 0 ? (
                <p className="text-center text-sm text-foreground/40 py-8">
                  {searchQuery ? 'No matching chats' : 'No chats found'}
                </p>
              ) : (
                filteredChats.map((chat, index) => {
                  const phoneNumber = extractPhoneNumber(chat.jid);
                  const hasNoName = isPhoneNumberOnly(chat.name, chat.jid);
                  const displayName = hasNoName ? formatPhoneNumber(phoneNumber) : chat.name;
                  const avatarLetter = hasNoName 
                    ? '#' // Show # for unknown contacts
                    : chat.name.charAt(0).toUpperCase();
                  
                  return (
                    <motion.div
                      key={chat.jid}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ delay: index * 0.02 }}
                      onClick={() => toggleChat(chat.jid)}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all",
                        selectedJids.has(chat.jid)
                          ? "bg-[#25D366]/10 border border-[#25D366]/20"
                          : "bg-foreground/5 border border-transparent hover:bg-foreground/10"
                      )}
                    >
                      {/* Avatar */}
                      <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium shrink-0",
                        chat.isGroup 
                          ? "bg-blue-500/10 text-blue-500" 
                          : hasNoName
                            ? "bg-foreground/10 text-foreground/50" // Muted for unknown
                            : "bg-[#25D366]/10 text-[#25D366]"
                      )}>
                        {chat.isGroup ? (
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 12.75c1.63 0 3.07.39 4.24.9 1.08.48 1.76 1.56 1.76 2.73V18H6v-1.61c0-1.18.68-2.26 1.76-2.73 1.17-.52 2.61-.91 4.24-.91zM4 13c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm1.13 1.1c-.37-.06-.74-.1-1.13-.1-.99 0-1.93.21-2.78.58A2.01 2.01 0 000 16.43V18h4.5v-1.61c0-.83.23-1.61.63-2.29zM20 13c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm4 3.43c0-.81-.48-1.53-1.22-1.85A6.95 6.95 0 0020 14c-.39 0-.76.04-1.13.1.4.68.63 1.46.63 2.29V18H24v-1.57zM12 6c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3z"/>
                          </svg>
                        ) : (
                          avatarLetter
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={cn(
                            "text-sm font-medium truncate",
                            hasNoName ? "text-foreground/70 font-mono" : "text-foreground"
                          )}>
                            {displayName}
                          </p>
                          {hasNoName && !chat.isGroup && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-foreground/5 text-foreground/40 rounded shrink-0">
                              No name saved
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-foreground/40">
                          {/* Show phone number if we have a name (so user can identify) */}
                          {!hasNoName && !chat.isGroup && (
                            <span className="font-mono">+{phoneNumber}</span>
                          )}
                          {!hasNoName && !chat.isGroup && chat.lastMessageTime && (
                            <span>•</span>
                          )}
                          {chat.lastMessageTime 
                            ? formatRelativeTime(chat.lastMessageTime)
                            : 'No messages'}
                        </div>
                      </div>

                      {/* Checkbox */}
                      <div className={cn(
                        "w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all shrink-0",
                        selectedJids.has(chat.jid)
                          ? "bg-[#25D366] border-[#25D366]"
                          : "border-foreground/20"
                      )}>
                        {selectedJids.has(chat.jid) && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </motion.div>
                  );
                })
              )}
            </AnimatePresence>
          </div>

          {/* Privacy note */}
          <p className="text-xs text-foreground/30 text-center flex-shrink-0">
            Message content is processed to build your relationship graph.
            <br />
            We never store raw message text.
          </p>

          {/* Actions — pinned, always reachable */}
          <div className="flex gap-3 flex-shrink-0">
            <button
              onClick={onSkip}
              disabled={isLoading}
              className="flex-1 py-3 rounded-xl text-sm font-medium text-foreground/50 hover:text-foreground hover:bg-foreground/5 transition-colors"
            >
              Skip for now
            </button>
            <button
              onClick={handleConfirm}
              disabled={isLoading || selectedJids.size === 0}
              className={cn(
                "flex-1 py-3 rounded-xl text-sm font-medium transition-all",
                selectedJids.size > 0
                  ? "bg-[#25D366] text-white hover:bg-[#25D366]/90"
                  : "bg-foreground/10 text-foreground/30 cursor-not-allowed"
              )}
            >
              {isLoading ? (
                <motion.div
                  className="w-5 h-5 mx-auto border-2 border-white/20 border-t-white rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                />
              ) : (
                `Sync ${selectedJids.size} chat${selectedJids.size !== 1 ? 's' : ''}`
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
